/**
 * Search Facets Component for v0.7.0
 * Displays dynamic facet counts for categories, regions, and price ranges
 */

import type { AdvancedSearchParams, SearchFacetsData } from '../../types';
import { CATEGORIES, REGIONS } from '../../types';
import './SearchFacets.css';

interface SearchFacetsProps {
  facets: SearchFacetsData | null;
  selectedParams: AdvancedSearchParams;
  onFacetClick: (params: AdvancedSearchParams) => void;
  className?: string;
}

const PRICE_RANGE_LABELS: Record<string, string> = {
  '$': '$ 以下 (500)',
  '$$': '$ - $$ (500-2000)',
  '$$$': '$ - $$$ (1000-5000)',
  '$$$$': '$$$ 以上 (5000+)',
};

const SearchFacets = ({
  facets,
  selectedParams,
  onFacetClick,
  className = '',
}: SearchFacetsProps) => {
  const handleCategoryClick = (category: string) => {
    // Toggle category selection
    const currentCategories = selectedParams.categories || [];
    const newCategories = currentCategories.includes(category)
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];

    onFacetClick({
      ...selectedParams,
      categories: newCategories.length > 0 ? newCategories : undefined,
      category: undefined,
    });
  };

  const handleRegionClick = (region: string) => {
    // Toggle region selection
    const currentRegions = selectedParams.regions || [];
    const newRegions = currentRegions.includes(region)
      ? currentRegions.filter(r => r !== region)
      : [...currentRegions, region];

    onFacetClick({
      ...selectedParams,
      regions: newRegions.length > 0 ? newRegions : undefined,
      region: undefined,
    });
  };

  const handlePriceRangeClick = (priceRange: string) => {
    // Map price range symbol to min/max values
    const priceMap: Record<string, { min: number | null; max: number | null }> = {
      '$': { min: null, max: 500 },
      '$$': { min: 500, max: 2000 },
      '$$$': { min: 1000, max: 5000 },
      '$$$$': { min: 5000, max: null },
    };

    onFacetClick({
      ...selectedParams,
      priceRange: priceMap[priceRange],
    });
  };

  const isSelectedCategory = (category: string) =>
    selectedParams.categories?.includes(category) || selectedParams.category === category;

  const isSelectedRegion = (region: string) =>
    selectedParams.regions?.includes(region) || selectedParams.region === region;

  const getFacetCount = (facetType: 'categories' | 'regions' | 'priceRanges', key: string) =>
    facets?.[facetType]?.[key] || 0;

  return (
    <div className={`search-facets ${className}`}>
      <div className="search-facets__header">
        <h3 className="search-facets__title">篩選條件</h3>
      </div>

      {facets && (
        <div className="search-facets__content">
          {/* Category Facets */}
          <div className="facet-section">
            <h4 className="facet-section__title">類別</h4>
            <div className="facet-list">
              {CATEGORIES.map((category) => {
                const count = getFacetCount('categories', category);
                const selected = isSelectedCategory(category);

                return (
                  <button
                    key={category}
                    className={`facet-item ${selected ? 'facet-item--selected' : ''} ${count === 0 ? 'facet-item--disabled' : ''}`}
                    onClick={() => count > 0 && handleCategoryClick(category)}
                    disabled={count === 0}
                  >
                    <span className="facet-item__label">{category}</span>
                    <span className="facet-item__count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Region Facets */}
          <div className="facet-section">
            <h4 className="facet-section__title">地區</h4>
            <div className="facet-list facet-list--scroll">
              {REGIONS.map((region) => {
                const count = getFacetCount('regions', region);
                const selected = isSelectedRegion(region);

                return (
                  <button
                    key={region}
                    className={`facet-item ${selected ? 'facet-item--selected' : ''} ${count === 0 ? 'facet-item--disabled' : ''}`}
                    onClick={() => count > 0 && handleRegionClick(region)}
                    disabled={count === 0}
                  >
                    <span className="facet-item__label">{region}</span>
                    <span className="facet-item__count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Price Range Facets */}
          <div className="facet-section">
            <h4 className="facet-section__title">價格區間</h4>
            <div className="facet-list">
              {Object.entries(PRICE_RANGE_LABELS).map(([key, label]) => {
                const count = getFacetCount('priceRanges', key);
                const selected = selectedParams.priceRange?.min !== undefined &&
                  selectedParams.priceRange?.max !== undefined &&
                  ((key === '$' && selectedParams.priceRange.max === 500) ||
                   (key === '$$' && selectedParams.priceRange.min === 500 && selectedParams.priceRange.max === 2000) ||
                   (key === '$$$' && selectedParams.priceRange.min === 1000 && selectedParams.priceRange.max === 5000) ||
                   (key === '$$$$' && selectedParams.priceRange.min === 5000));

                return (
                  <button
                    key={key}
                    className={`facet-item ${selected ? 'facet-item--selected' : ''} ${count === 0 ? 'facet-item--disabled' : ''}`}
                    onClick={() => count > 0 && handlePriceRangeClick(key)}
                    disabled={count === 0}
                  >
                    <span className="facet-item__label">{label}</span>
                    <span className="facet-item__count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFacets;
